"use client"

import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { StorePromotion } from "@/types/store"
import { X } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import toast, { Toaster } from "react-hot-toast"
import { useTranslation } from "react-i18next"

const BACKEND_URL = process.env.NEXT_PUBLIC_MEDUSA_BACKEND_URL

// Request deduplication: prevent multiple simultaneous requests
let pendingRequest: Promise<Response> | null = null
let lastRequestTime = 0
const REQUEST_COOLDOWN = 1000 // 1 second cooldown between requests

export default function DiscountPopup() {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)
  const [dontShowAgain, setDontShowAgain] = useState(false)
  const [showPopup, setShowPopup] = useState(false)
  const [featuredPromotion, setFeaturedPromotion] = useState<StorePromotion | null>(null)
  const isMountedRef = useRef(true)

  useEffect(() => {
    const fetchPromotion = async () => {
      // Prevent multiple simultaneous requests
      const now = Date.now()
      if (now - lastRequestTime < REQUEST_COOLDOWN && pendingRequest) {
        try {
          const response = await pendingRequest
          if (!isMountedRef.current) return

          if (!response.ok) {
            throw new Error(`Failed to fetch featured promotion: ${response.statusText}`)
          }

          const data = await response.json()
          if (data?.promotion) {
            setFeaturedPromotion(data.promotion)
          }
        } catch (error) {
          if (isMountedRef.current) {
            console.error("Error fetching featured promotion:", error)
          }
        }
        return
      }

      try {
        lastRequestTime = now
        pendingRequest = fetch(`${BACKEND_URL}/store/featured-promotion`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        })

        const response = await pendingRequest

        // Clear pending request after completion
        pendingRequest = null

        if (!isMountedRef.current) return

        if (!response.ok) {
          throw new Error(`Failed to fetch featured promotion: ${response.statusText}`)
        }

        const data = await response.json()
        if (data?.promotion) {
          setFeaturedPromotion(data.promotion)
        }
      } catch (error) {
        pendingRequest = null
        if (isMountedRef.current) {
          console.error("Error fetching featured promotion:", error)
        }
      }
    }

    fetchPromotion()

    return () => {
      isMountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (!featuredPromotion) return

    const hiddenPromotionId = localStorage.getItem("hiddenPromotionId")
    const isHidden = Number(hiddenPromotionId) === Number(featuredPromotion.id)

    if (!isHidden) {
      const timer = setTimeout(() => setShowPopup(true), 1500)
      return () => clearTimeout(timer)
    }
  }, [featuredPromotion])

  const handleCopy = async () => {
    if (!featuredPromotion) return
    try {
      await navigator.clipboard.writeText(featuredPromotion.code)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
      toast.success(t("promotion.copiedToClipboard"))
    } catch (err) {
      console.error("Failed to copy: ", err)
    }
  }

  const handleDontShowAgain = (checked: boolean) => {
    if (!featuredPromotion) return
    setDontShowAgain(checked)
    if (checked) {
      localStorage.setItem("hiddenPromotionId", featuredPromotion.id)
    } else {
      localStorage.removeItem("hiddenPromotionId")
    }
  }

  if (!showPopup || !featuredPromotion?.id) return null

  return (
    <div>
      <div className="fixed bottom-4 left-4 z-50">
        <div className="relative w-96 max-w-md overflow-hidden rounded-lg border border-ui-border-default bg-ui-bg-subtle shadow-lg">
          {/* Close Button */}
          <button
            onClick={() => setShowPopup(false)}
            className="absolute top-2 right-2 p-2 text-gray-400 hover:text-gray-600"
            aria-label={t("promotion.closePopup")}
          >
            <X className="h-4 w-4" />
          </button>

          {/* Content */}
          <div className="p-6 pb-4">
            <h2 className="mb-6 text-center text-2xl font-light text-slate-700">
              {featuredPromotion.feature_description}
            </h2>

            <div className="mb-4 flex items-center justify-center rounded-md border border-dashed border-gray-300 bg-gray-50 p-3">
              <span className="text-xl font-medium text-slate-700">
                {featuredPromotion.code}
              </span>
            </div>

            <Button
              className="mb-4 w-full rounded-md bg-orange-500 py-3 text-slate-900 hover:bg-orange-600"
              onClick={handleCopy}
            >
              {t("promotion.copy")}
            </Button>

            <div className="flex items-center justify-center gap-2 text-gray-400">
              <span>{t("promotion.dontShowAgain")}</span>
              <Checkbox
                id="dont-show-again"
                checked={dontShowAgain}
                onCheckedChange={handleDontShowAgain}
                className="h-5 w-5 border-gray-400"
              />
            </div>
          </div>
        </div>
      </div>
      <Toaster position="top-right" />
    </div>
  )
}
